/**
 * Discount rules for every screen that takes or edits an order.
 *
 *   • Nothing is ever negative — no price, no line, no total. Money off is a
 *     DISCOUNT, never a minus price (DP07223 shipped with a ₹-796 product line).
 *   • Every way of giving money off counts towards the same figure: the Discount
 *     box, a price typed below the product's list price, and a line ticked Free.
 *   • Above ₹100 off in total, a MANAGER must approve. A manager's own order is
 *     approved by them; anyone else's waits, and cannot reach a courier until a
 *     manager approves it (dispatchReadiness).
 *   • Above ₹100 off AND a product or the whole order down to ₹0 → a loud warning,
 *     because that is how a "free" order slips through by accident.
 */

export const DISCOUNT_APPROVAL_LIMIT = 100;
export const DISCOUNT_APPROVER_ROLES = new Set(['admin', 'super_admin', 'manager']);

const money = (n) => Math.round((Number(n) || 0) * 100) / 100;

export const canApproveDiscount = (role) => DISCOUNT_APPROVER_ROLES.has(String(role || '').toLowerCase());

/**
 * @param {{lines: {name?:string, listPrice:number, unitPrice:number, qty:number, free?:boolean}[], discount?:number}} p
 */
export function discountSummary({ lines = [], discount = 0 } = {}) {
    const negatives = [];
    const zeroLines = [];
    let listTotal = 0, lineCut = 0, linesTotal = 0;
    for (const l of lines) {
        const qty = Math.max(1, Number(l.qty) || 1);
        const list = money(l.listPrice);
        const unit = l.free ? 0 : money(l.unitPrice);
        if (unit < 0 || list < 0) negatives.push(l.name || 'A product');
        if (unit === 0 && list > 0) zeroLines.push(l.name || 'A product');
        listTotal += list * qty;
        linesTotal += unit * qty;
        lineCut += Math.max(0, list - unit) * qty;
    }
    const orderDiscount = money(discount);
    if (orderDiscount < 0) negatives.push('Discount');
    listTotal = money(listTotal); linesTotal = money(linesTotal); lineCut = money(lineCut);
    const payable = money(linesTotal - Math.max(0, orderDiscount));
    if (payable < 0) negatives.push('Order total');
    return {
        listTotal, linesTotal, lineCut, orderDiscount,
        totalCut: money(lineCut + Math.max(0, orderDiscount)),
        payable,
        zeroLines,
        negatives,
    };
}

/**
 * What the screen must do about a discount.
 * @returns {{blocked:string|null, needsApproval:boolean, approvedBySelf:boolean, warnings:string[]}}
 */
export function discountDecision(summary, role) {
    const s = summary;
    if (s.negatives.length) {
        return { blocked: `${s.negatives.join(', ')} cannot be below ₹0. For money off use the Discount box, never a minus price.`, needsApproval: false, approvedBySelf: false, warnings: [] };
    }
    const over = s.totalCut > DISCOUNT_APPROVAL_LIMIT;
    const manager = canApproveDiscount(role);
    const warnings = [];
    if (over) {
        warnings.push(manager
            ? `₹${s.totalCut} off — above the ₹${DISCOUNT_APPROVAL_LIMIT} limit. You are a manager, so this is approved by you.`
            : `₹${s.totalCut} off — above the ₹${DISCOUNT_APPROVAL_LIMIT} limit. The order will WAIT for a manager's approval and cannot ship until then.`);
        if (s.payable === 0) warnings.push('⚠ The customer pays ₹0 for this order.');
        else if (s.zeroLines.length) warnings.push(`⚠ ${s.zeroLines.join(', ')} ${s.zeroLines.length > 1 ? 'are' : 'is'} at ₹0.`);
    }
    return { blocked: null, needsApproval: over, approvedBySelf: over && manager, warnings };
}

/** The approval record to store on a new or edited order, or null when none is needed. */
export function discountApprovalRecord(summary, { role, agentName, now = Date.now() }) {
    if (!(summary.totalCut > DISCOUNT_APPROVAL_LIMIT)) return null;
    const base = { amount: summary.totalCut, limit: DISCOUNT_APPROVAL_LIMIT, requestedBy: agentName || '', requestedAt: now, payable: summary.payable };
    return canApproveDiscount(role)
        ? { ...base, status: 'approved', approvedBy: agentName || '', approvedAt: now, self: true }
        : { ...base, status: 'pending' };
}

export const discountApprovalPending = (order) => String(order?.discountApproval?.status || '') === 'pending';
